import React, { useEffect, useMemo, useRef, useState } from 'react'
import { utilsEditor, utilsMaterial } from '@amaspace-editor/editor-3d'
import assetApi, {ASSET_TYPE, AssetType, useGetAssetByIdQuery, useGetAssetsQuery} from '../../../../api/assetApi'
import { Col, Skeleton } from 'antd'
import styled from 'styled-components'
import { TAsset } from '../../../../types'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../../hooks'
import {
    changeDeleteConnections,
    changeNewConnections,
    modelDeleteConnections,
    modelNewConnections
} from '../../../../slices/modelSlice'

interface AttachAssetProps {
    keyMap: string
    value: any
    onPropChange: (key: string, value: AttachAssetProps['value']) => void
    asset?: any
}

interface RenderMapProps {
    preview?: string
    nodeAsset: AMAModelConfig | null
    asset: any
    activeNode: any
}

interface RenderMapIdProps {
    value?: AttachAssetProps['value']
    activeNode: any
    asset: any
}

export type ModelConnections = {
    asset_from: string
    asset_to: string
}
export type AMAModelConfig = {
    assets: TAsset[]
    connections: ModelConnections[]
}

const RenderMap = ({ preview = '', nodeAsset, asset, activeNode }: RenderMapProps) => {
    const editor = utilsEditor()
    const materialEditor = utilsMaterial()
    const dispatch = useAppDispatch()
    const newModelConnections = useSelector(modelNewConnections)
    const deleteModelConnections = useSelector(modelDeleteConnections)

    const [modalToggle, setModalToggle] = useState<boolean>(false)

    const mapRef = useRef<HTMLDivElement | null>(null)

    const { data: assetsData, isFetching: isFetchingAssets, refetch } = useGetAssetsQuery({ type: ASSET_TYPE.Material })

    const valid = useMemo((): number => {
        if (!mapRef.current) return 0
        return window.innerHeight - (mapRef.current.getBoundingClientRect().bottom + 335)
    }, [mapRef.current, modalToggle])

    useEffect(() => {
        if (assetsData) {
            refetch()
        }
    }, [modalToggle])

    const updateMap = async (assetMap: AssetType) => {
        if (!editor.activeMesh) return

        const res = (await dispatch(assetApi.endpoints.getAssetById.initiate({ id: assetMap.id }))) as any
        const { data } = (await dispatch(assetApi.endpoints.getAssetById.initiate({ id: activeNode.id }))) as any

        const nodeAsset = data
        const targetAsset = nodeAsset.assets[0]

        if (!data.connections) {
            const mapAsset = res.data

            const connectModelToMaterial = {
                asset_to: asset.id,
                asset_from: targetAsset.id
            }
            const connectMaterialToTexture = {
                asset_to: targetAsset.id,
                asset_from: assetMap.id
            }
            const objForUpdate = materialEditor.createMaterialFromAsset(mapAsset)

            editor.updateObjectData(editor.activeMesh, { material: objForUpdate })
            editor.updateUploadedModelData(editor.activeMesh, { material: objForUpdate })
            dispatch(
                changeNewConnections({
                    ...newModelConnections,
                    [targetAsset.id]: [connectModelToMaterial, connectMaterialToTexture]
                })
            )

            return
        }
        const connect = {
            asset_to: targetAsset.id,
            asset_from: assetMap.id
        }

        const isConnections = res.data.connections ? res.data.connections : []
        const newConnections = [connect, ...isConnections]
        const mapAsset = res.data as any
        const objForUpdate = materialEditor.createMaterialFromAsset(mapAsset)
        const oldConnections = nodeAsset.connections.filter((item: ModelConnections) => {
            if (item.asset_to === targetAsset.id) return item
        })

        dispatch(changeDeleteConnections({ ...deleteModelConnections, [targetAsset.id]: oldConnections }))
        dispatch(changeNewConnections({ ...newModelConnections, [targetAsset.id]: newConnections }))

        editor.updateObjectData(editor.activeMesh, { material: objForUpdate })
        editor.updateUploadedModelData(editor.activeMesh, { material: objForUpdate })
    }
    return (
        <MapIcon onClick={() => setModalToggle(true)} ref={mapRef} url={preview}>
            {modalToggle ? (
                <>
                    <TextureAssetsOverlay
                        onClick={e => {
                            e.stopPropagation()
                            setModalToggle(false)
                        }}
                    />
                    <TextureAssetsWrapper position={valid}>
                        {!isFetchingAssets ? (
                            assetsData &&
                            assetsData.map(item => {
                                return (
                                    <TextureItem key={item.id} onClick={() => updateMap(item)}>
                                        <MapIcon url={item.preview!} />
                                        <TextureTitle>{item.name}</TextureTitle>
                                    </TextureItem>
                                )
                            })
                        ) : (
                            <Skeleton paragraph={{ rows: 7 }} />
                        )}
                    </TextureAssetsWrapper>
                </>
            ) : null}
        </MapIcon>
    )
}

const RenderEmptyMap = ({ activeNode, asset }: RenderMapIdProps) => {
    const [nodeAsset, setNodeAsset] = useState<AMAModelConfig | null>(null)
    const nodeAssetResponse = useGetAssetByIdQuery({ id: activeNode.id as string })

    useEffect(() => {
        if (nodeAssetResponse) {
            const { data } = nodeAssetResponse as { data: AMAModelConfig }
            if (!data) return

            setNodeAsset(data)
        }
    }, [nodeAssetResponse])
    return <RenderMap nodeAsset={nodeAsset} asset={asset} activeNode={activeNode} />
}

const RenderMapId = ({ value, activeNode, asset }: RenderMapIdProps) => {
    const [preview, setPreview] = useState<string>('')
    const [nodeAsset, setNodeAsset] = useState<AMAModelConfig | null>(null)

    const fetchAssetByIdResponse = useGetAssetByIdQuery({ id: value?.id as string })
    const nodeAssetResponse = useGetAssetByIdQuery({ id: activeNode.id as string })

    useEffect(() => {
        if (fetchAssetByIdResponse) {
            const { data } = fetchAssetByIdResponse

            if (!data) return
            const { assets } = data as { assets: TAsset[] }
            const targetAsset = assets[0]
            setPreview(targetAsset.preview || '')
        }
    }, [fetchAssetByIdResponse])

    useEffect(() => {
        if (nodeAssetResponse) {
            const { data } = nodeAssetResponse as { data: AMAModelConfig }
            if (!data) return
            setNodeAsset(data)
        }
    }, [nodeAssetResponse])

    return <RenderMap preview={preview} nodeAsset={nodeAsset} asset={asset} activeNode={activeNode} />
}

const RenderAttachAsset = ({ keyMap, value, onPropChange, asset }: AttachAssetProps) => {
    const editor = utilsEditor()

    const configId = useMemo(() => {
        if (!editor.activeMesh) return ''
        return editor.getCurrentConfigObj(editor.activeMesh) || ''
    }, [editor.activeMesh])

    return (
        <PropsItem span={16}>
            {value?.id && configId ? (
                <RenderMapId asset={asset} value={value} activeNode={configId} />
            ) : (
                <RenderEmptyMap activeNode={configId} asset={asset} />
            )}
        </PropsItem>
    )
}

export default RenderAttachAsset

const PropsItem = styled(Col)`
    display: flex;
    align-items: center;
    gap: 20px;
`

const TextureAssetsWrapper = styled.div<{ position: number }>`
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
    top: ${props => (props.position < 0 ? `${props.position}px` : '-5px')};
    left: -5px;
    border: 1px solid #d9d9d9;
    border-radius: 5px;
    position: relative;
    padding: 5px;
    height: 300px;
    width: 200px;
    background: #fff;
    backdrop-filter: blur(20px);
    z-index: 20;
    overflow-y: auto;
`
const TextureTitle = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`
const TextureItem = styled(PropsItem)`
    transition: background-color 0.3s ease-in-out;
    gap: 10px;
    cursor: pointer;

    &:hover {
        background-color: rgb(236 236 236);
    }
`
const TextureAssetsOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: 10;
`

const MapIcon = styled.div<{ url: string }>`
    width: 60px;
    height: 30px;
    border: 1px solid #000;
    background: ${props => `no-repeat center/cover url(${props?.url})`};
    flex-shrink: 0;
`
